import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import SummaryApi from "../common";

// ────────────────────────────────────────────────
// THEME — same rules as AllCities.js / InterestedStudents.js
// primary → header text, links, primary buttons
// ────────────────────────────────────────────────
const THEME = {
  primary: 'rgb(23, 44, 87)',        // #172C57
  primaryLight: 'rgba(23, 44, 87, 0.08)',
  actionGreen: '#10B981',
  actionYellow: '#D97706',
};

const toList = (data) => (Array.isArray(data) ? data : data?.data || []);

// "2026-10-05 09:50:54.000000" -> { date: "05-10-2026", time: "09:50" }
const splitDateTime = (value) => {
  if (!value) return { date: "-", time: "" };
  const [date, time = ""] = String(value).split(" ");
  const [y, m, d] = date.split("-");
  if (!y || !m || !d) return { date: String(value), time: "" };
  return { date: `${d}-${m}-${y}`, time: time.slice(0, 5) };
};

const formatNumber = (n) => Number(n || 0).toLocaleString("en-IN");

const inputClass =
  "block w-full h-10 px-3 text-sm text-gray-800 bg-white border border-gray-300 rounded-lg cursor-pointer shadow-sm focus:outline-none focus:ring-2 focus:ring-[#172C57] focus:border-[#172C57]";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="block mb-1 text-xs font-semibold"
      style={{ color: THEME.primary }}
    >
      {label}
    </label>
    {children}
  </div>
);

const StatCard = ({ label, value, valueClass }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
    <p className="text-sm text-gray-500">{label}</p>
    <p
      className={`mt-1 text-2xl font-bold tabular-nums ${valueClass || ""}`}
      style={!valueClass ? { color: THEME.primary } : undefined}
    >
      {formatNumber(value)}
    </p>
  </div>
);

const COLUMNS = [
  { label: "S.No", align: "text-left" },
  { label: "Region", align: "text-left" },
  { label: "District", align: "text-left" },
  { label: "Description", align: "text-left" },
  { label: "In file", align: "text-right" },
  { label: "Inserted", align: "text-right" },
  { label: "Duplicates skipped", align: "text-right" },
  { label: "Uploaded by", align: "text-left" },
  { label: "Uploaded on", align: "text-left" },
];

const AllUploadedData = () => {
  const [records, setRecords] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const authHeaders = { Authorization: "Bearer sandeep_university_916" };

  // filters
  const [region, setRegion] = useState("");
  const [districtId, setDistrictId] = useState("");
  const [universityId, setUniversityId] = useState("");
  const [uploadedBy, setUploadedBy] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;

    const request = async (api) => {
      const res = await fetch(api.url, {
        method: api.method,
        headers: authHeaders,
      });
      return res.json();
    };

    const loadAll = async () => {
      setLoading(true);

      const [uploadsR, distR] = await Promise.allSettled([
        request(SummaryApi.getAllUpdataDetails),
        // request(SummaryApi.getactiveuniversity),
        request(SummaryApi.GetAllDisticts),
      ]);

      if (cancelled) return;

      const failed = [];

      const apply = (result, label, setter, allowEmpty = false) => {
        if (result.status === "rejected") {
          console.error(`Failed to load ${label}:`, result.reason);
          failed.push(label);
          return;
        }
        if (result.value?.status === false && !allowEmpty) {
          console.error(`Failed to load ${label}:`, result.value?.message);
          failed.push(label);
          return;
        }
        setter(toList(result.value));
      };

      apply(uploadsR, "upload details", setRecords, true);
      // apply(uniR, "universities", setUniversities);
      apply(distR, "districts", setDistricts);

      if (failed.length) {
        toast.error(`Failed to load ${failed.join(" and ")}`, {
          toastId: "uploaded-data-load-error",
        });
      }
      setLoading(false);
    };

    loadAll();
    return () => {
      cancelled = true;
    };
  }, []);

  // id -> name lookups
  const universityName = useMemo(() => {
    const map = {};
    universities.forEach((u) => {
      map[String(u.university_id)] = u.university_name;
    });
    return map;
  }, [universities]);

  const districtName = useMemo(() => {
    const map = {};
    districts.forEach((d) => {
      map[String(d.district_id)] = d.district_name;
    });
    return map;
  }, [districts]);

  const regionOptions = useMemo(() => {
    const set = new Set(
      records.map((r) => (r.region || "").trim()).filter(Boolean),
    );
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [records]);

  const districtOptions = useMemo(() => {
    const ids = [...new Set(records.map((r) => String(r.district_id)))];
    return ids
      .map((id) => ({ id, name: districtName[id] || `ID ${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [records, districtName]);

  const universityOptions = useMemo(() => {
    const ids = [...new Set(records.map((r) => String(r.university_id)))];
    return ids
      .map((id) => ({ id, name: universityName[id] || `ID ${id}` }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [records, universityName]);

  const uploadedByOptions = useMemo(() => {
    const set = new Set(
      records.map((r) => (r.uploaded_by || "").trim()).filter(Boolean),
    );
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [records]);

  const filtered = useMemo(() => {
    return records.filter((r) => {
      if (region && (r.region || "").trim() !== region) return false;
      if (districtId && String(r.district_id) !== districtId) return false;
      if (universityId && String(r.university_id) !== universityId)
        return false;
      if (uploadedBy && (r.uploaded_by || "").trim() !== uploadedBy)
        return false;

      const day = String(r.created_date || "").slice(0, 10);
      if (fromDate && day < fromDate) return false;
      if (toDate && day > toDate) return false;
      return true;
    });
  }, [records, region, districtId, universityId, uploadedBy, fromDate, toDate]);

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => {
          const uploaded = Number(r.uploaded_count) || 0;
          const inserted = Number(r.inserted_count) || 0;
          acc.uploaded += uploaded;
          acc.inserted += inserted;
          acc.duplicates += Math.max(uploaded - inserted, 0);
          return acc;
        },
        { uploaded: 0, inserted: 0, duplicates: 0 },
      ),
    [filtered],
  );

  const activeCount = [
    region,
    districtId,
    universityId,
    uploadedBy,
    fromDate,
    toDate,
  ].filter(Boolean).length;
  const hasFilters = activeCount > 0;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [region, districtId, universityId, uploadedBy, fromDate, toDate]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const startIndex = (page - 1) * PAGE_SIZE;
  const paginated = useMemo(
    () => filtered.slice(startIndex, startIndex + PAGE_SIZE),
    [filtered, startIndex],
  );

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = new Set([1, totalPages, page - 1, page, page + 1]);
    const sorted = [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
    const result = [];
    sorted.forEach((p, i) => {
      if (i > 0 && p - sorted[i - 1] > 1) result.push("...");
      result.push(p);
    });
    return result;
  }, [page, totalPages]);

  const clearFilters = () => {
    setRegion("");
    setDistrictId("");
    setUniversityId("");
    setUploadedBy("");
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="min-h-screen pt-4">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1
            className="text-2xl font-bold"
            style={{ color: THEME.primary }}
          >
            Uploaded data
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Every Excel upload, with how many records were added and how many
            were skipped as duplicates.
          </p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Uploads" value={filtered.length} />
          <StatCard label="Records in files" value={totals.uploaded} />
          <StatCard
            label="Records inserted"
            value={totals.inserted}
            valueClass="text-green-700"
          />
          <StatCard
            label="Duplicates skipped"
            value={totals.duplicates}
            valueClass={totals.duplicates > 0 ? "text-yellow-700" : ""}
          />
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2
                className="text-sm font-bold"
                style={{ color: THEME.primary }}
              >
                Filters
              </h2>
              {hasFilters && (
                <span
                  className="px-2 py-0.5 text-xs font-semibold rounded-full"
                  style={{
                    backgroundColor: THEME.primaryLight,
                    color: THEME.primary,
                  }}
                >
                  {activeCount} active
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={clearFilters}
              disabled={!hasFilters}
              style={hasFilters ? { color: THEME.primary } : undefined}
              className="text-sm font-semibold hover:opacity-80 disabled:text-gray-300 disabled:cursor-not-allowed transition-opacity"
            >
              Clear all
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">


            <Field label="Region" htmlFor="f-region">
              <select
                id="f-region"
                className={inputClass}
                value={region}
                onChange={(e) => setRegion(e.target.value)}
              >
                <option value="">All regions</option>
                {regionOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="District" htmlFor="f-district">
              <select
                id="f-district"
                className={inputClass}
                value={districtId}
                onChange={(e) => setDistrictId(e.target.value)}
              >
                <option value="">All districts</option>
                {districtOptions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Uploaded by" htmlFor="f-uploaded-by">
              <select
                id="f-uploaded-by"
                className={inputClass}
                value={uploadedBy}
                onChange={(e) => setUploadedBy(e.target.value)}
              >
                <option value="">All uploaders</option>
                {uploadedByOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="From date" htmlFor="f-from">
              <input
                id="f-from"
                type="date"
                className={inputClass}
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Field>

            <Field label="To date" htmlFor="f-to">
              <input
                id="f-to"
                type="date"
                className={inputClass}
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Field>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h2
              className="text-sm font-bold"
              style={{ color: THEME.primary }}
            >
              Upload history
            </h2>
            <p className="text-sm text-gray-500">
              Showing {filtered.length === 0 ? 0 : startIndex + 1}–
              {Math.min(startIndex + PAGE_SIZE, filtered.length)} of {filtered.length}
              {filtered.length !== records.length && ` (filtered from ${records.length})`}
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.label}
                      className={`px-4 py-3 font-semibold whitespace-nowrap ${c.align}`}
                      style={{ color: THEME.primary }}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {COLUMNS.map((c) => (
                        <td key={c.label} className="px-4 py-4">
                          <div className="h-4 bg-gray-100 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-4 py-14 text-center">
                      <p className="text-gray-700 font-medium">
                        {hasFilters
                          ? "No uploads match these filters"
                          : "No uploads yet"}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        {hasFilters
                          ? "Try removing a filter or widening the date range."
                          : "Uploaded files will show up here."}
                      </p>
                      {hasFilters && (
                        <button
                          type="button"
                          onClick={clearFilters}
                          style={{ backgroundColor: THEME.primary }}
                          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                          className="mt-4 px-4 py-2 text-sm font-semibold text-white rounded-lg transition-opacity"
                        >
                          Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : (
                  paginated.map((r, index) => {
                    const uploaded = Number(r.uploaded_count) || 0;
                    const inserted = Number(r.inserted_count) || 0;
                    const duplicates = Math.max(uploaded - inserted, 0);
                    const uploader = (r.uploaded_by || "").trim();
                    const { date, time } = splitDateTime(r.created_date);

                    return (
                      <tr key={r.updata_details_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-500 tabular-nums">
                          {startIndex + index + 1}
                        </td>

                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {r.region || "-"}
                        </td>
                        <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                          {districtName[String(r.district_id)] ||
                            `ID ${r.district_id}`}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <div
                            className="max-w-xs truncate"
                            title={r.description || ""}
                          >
                            {r.description || "-"}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 tabular-nums">
                          {formatNumber(uploaded)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-green-700 bg-green-50 rounded-full tabular-nums">
                            {formatNumber(inserted)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {duplicates > 0 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-yellow-700 bg-yellow-50 rounded-full tabular-nums">
                              {formatNumber(duplicates)}
                            </span>
                          ) : (
                            <span className="text-gray-400 tabular-nums">0</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {uploader ? (
                            <div className="flex items-center gap-2">
                              <span
                                className="flex items-center justify-center w-7 h-7 text-xs font-bold rounded-full"
                                style={{
                                  backgroundColor: THEME.primaryLight,
                                  color: THEME.primary,
                                }}
                              >
                                {uploader.charAt(0).toUpperCase()}
                              </span>
                              <span className="text-gray-800">{uploader}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">Not recorded</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-gray-800">{date}</div>
                          {time && (
                            <div className="text-xs text-gray-500">{time}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {!loading && filtered.length > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-gray-200">
              <p
                className="text-sm font-medium"
                style={{ color: THEME.primary }}
              >
                Page {page} of {totalPages}
              </p>

              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{ color: THEME.primary }}
                  className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Previous
                </button>

                {pageNumbers.map((p, i) =>
                  p === "..." ? (
                    <span key={`dots-${i}`} className="px-2 text-gray-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPage(p)}
                      style={
                        p === page
                          ? { backgroundColor: THEME.primary, borderColor: THEME.primary }
                          : { color: THEME.primary }
                      }
                      className={`min-w-[2.25rem] px-3 py-1.5 text-sm font-medium rounded-lg border ${p === page
                        ? "text-white"
                        : "bg-white border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      {p}
                    </button>
                  ),
                )}

                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{ color: THEME.primary }}
                  className="px-3 py-1.5 text-sm font-medium bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:bg-white"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllUploadedData;